import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useToast,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  ContractDateReminderType,
  ContractDto,
  ContractNotificationDurationType,
  PersonDto,
  useListParticipantsForContractsQuery,
  useUpdateContractMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { PersonsSelectorForm } from "../../../../common/persons/PersonsSelectorForm";
import { NotificationTimeSelector } from "./NotificationTimeSelector";

interface Props {
  data: ContractDto;
  onClose: () => void;
}

interface FormValues {
  event: ContractDateReminderType;
  notificationTimeAmount: number;
  notificationTimeUnit: ContractNotificationDurationType;
  persons: PersonDto[];
}

export const ContractNotificationModal = ({ data, onClose }: Props) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      event: ContractDateReminderType.NoEndDateReminder,
      notificationTimeUnit: ContractNotificationDurationType.Days,
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [addNotification] = useUpdateContractMutation();
  const toast = useToast();
  const displayer = useApiError();
  const { data: participants } = useListParticipantsForContractsQuery({ contractId: data.id });

  const create = async (values: FormValues) => {
    setIsLoading(true);
    try {
      await addNotification({
        contractId: data.id,
        updateContractRequest: {
          addDateReminderNotification: {
            inAdvanceUnit: values.notificationTimeUnit,
            inAdvanceAmount: values.notificationTimeAmount,
            receiverIds: values.persons.map((p) => p.id),
            dateReminderType: values.event,
          },
        },
      }).unwrap();
      setIsLoading(false);
      toast({
        title: t("Notification added!"),
        status: "success",
      });
      onClose();
    } catch (e) {
      setIsLoading(false);
      displayer.trigger(e);
    }
  };

  const eventType = methods.watch("event");

  const notificationNames = {
    EndDateReminder: t("End date reminder"),
    RenewalDateReminder: t("Renewal date reminder"),
    WarrantyExpirationDateReminder: t("Warranty expiration date reminder"),
    StartDateReminder: t("Start date reminder"),
    TerminationDateReminder: t("Termination date reminder"),
    NoticePeriodReminder: t("Notice period reminder"),
    OptionExecutionDeadlineReminder: t("Option execution deadline reminder"),
    OptionStartDateReminder: t("Option start date reminder"),
    NoEndDateReminder: t("Contract without end date reminder"),
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Add new reminder")}</ModalHeader>
        <ModalCloseButton />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(create)}>
            <ModalBody>
              <FormControl id="event" mt="5px" isInvalid={methods.formState.errors.event != null}>
                <FormLabel>{t("Reminder type")}</FormLabel>
                <Select
                  {...methods.register("event", {
                    required: {
                      value: true,
                      message: t("Please select a reminder type"),
                    },
                  })}
                >
                  {notificationOptions.map((o) => (
                    <option key={o} value={o}>
                      {notificationNames[o]}
                    </option>
                  ))}
                </Select>
                <FormHelperText>{t("The type of reminder that will be sent")}</FormHelperText>
                <FormErrorMessage>{methods.formState.errors.event?.message}</FormErrorMessage>
              </FormControl>
              <Flex pt="6">
                {participants && (
                  <PersonsSelectorForm<"persons", FormValues>
                    organizationIds={[
                      ...[participants.owningOrganization.id],
                      ...(participants?.organizationParticipants.map((p) => p.id) ?? []),
                    ]}
                    label={t("Receivers")}
                    control={methods.control}
                    name="persons"
                    placeholderText={`${t("Select receivers")}...`}
                    errorMessage={methods.formState.errors.persons?.message}
                    additionalPersons={participants.personParticipants}
                  />
                )}
              </Flex>
              {isDateReminder(eventType) && (
                <Flex pt="6">
                  <NotificationTimeSelector />
                </Flex>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isLoading}>
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                variant="solid"
                colorScheme="teal"
                isLoading={isLoading}
                isDisabled={!methods.formState.isValid}
              >
                {t("Create")}
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

const isDateReminder = (eventType: ContractDateReminderType) => {
  return (
    eventType === ContractDateReminderType.EndDateReminder ||
    eventType === ContractDateReminderType.RenewalDateReminder ||
    eventType === ContractDateReminderType.WarrantyExpirationDateReminder ||
    eventType === ContractDateReminderType.StartDateReminder ||
    eventType === ContractDateReminderType.TerminationDateReminder ||
    eventType === ContractDateReminderType.OptionStartDateReminder ||
    eventType === ContractDateReminderType.OptionExecutionDeadlineReminder
  );
};

const notificationOptions: ContractDateReminderType[] = [
  ContractDateReminderType.EndDateReminder,
  ContractDateReminderType.RenewalDateReminder,
  ContractDateReminderType.WarrantyExpirationDateReminder,
  ContractDateReminderType.StartDateReminder,
  ContractDateReminderType.TerminationDateReminder,
  ContractDateReminderType.OptionExecutionDeadlineReminder,
  ContractDateReminderType.OptionStartDateReminder,
];
